"use client";

import React, { useState, FC, createContext, useEffect } from "react";
import {
  signOutUser,
  onAuthStateChangedListener,
  getDocument,
} from "@/src/Firebase/firebase.utils";
import { usePathname } from "next/navigation";
import { User } from "firebase/auth";

type UserContextType = {
  user: {
    id: string | undefined;
    email: string | undefined;
    firstName: string | undefined;
    lastName: string | undefined;
    type: string | undefined;
    provider: string | undefined;
    examsCompleted: number;
    examsAttempted: number;
    imgId: string;
    imgUrl: string;
  };
  setUser: React.Dispatch<
    React.SetStateAction<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      type: string;
      provider: string;
      examsCompleted: number;
      examsAttempted: number;
      imgId: string;
      imgUrl: string;
    }>
  >;
  fetching: boolean | null;
  setfetching: React.Dispatch<React.SetStateAction<boolean | null>>;
  langSwitch: boolean;
  setLangSwitch: React.Dispatch<React.SetStateAction<boolean>>;
};

type Props = {
  children: React.ReactNode;
};

export const UserContext = createContext<UserContextType>({
  user: {
    id: "",
    email: "",
    firstName: "",
    lastName: "",
    type: "",
    provider: "",
    examsCompleted: 0,
    examsAttempted: 0,
    imgId: "",
    imgUrl: "",
  },
  setUser: () => {},
  fetching: null,
  setfetching: () => {},
  langSwitch: false,
  setLangSwitch: () => {},
});

const UserProvider: FC<Props> = ({ children }: Props) => {
  const [user, setUser] = useState({
    id: "",
    email: "",
    firstName: "",
    lastName: "",
    type: "",
    provider: "",
    examsCompleted: 0,
    examsAttempted: 0,
    imgId: "",
    imgUrl: "",
  });
  const [fetching, setfetching] = useState<boolean | null>(true);
  const [langSwitch, setLangSwitch] = useState(false);
  const pathname = usePathname();

  if (!pathname.includes("/welcome")) {
    if (typeof window !== "undefined") {
      const examCompleted = sessionStorage.getItem("examCompleted");
      if (examCompleted === "true") {
        sessionStorage.setItem("examCompleted", "false");
      }
    }

    if (typeof window !== "undefined") {
      const getCert = sessionStorage.getItem("getCert");
      if (getCert === "true") {
        sessionStorage.setItem("getCert", "false");
      }
    }
  }

  const clearUser = () => {
    setUser({
      id: "",
      email: "",
      firstName: "",
      lastName: "",
      type: "",
      provider: "",
      examsCompleted: 0,
      examsAttempted: 0,
      imgId: "",
      imgUrl: "",
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChangedListener(
      async (firebaseUser: User | null) => {
        setfetching(true);
        if (!firebaseUser) {
          clearUser();
          await signOutUser();
        } else if (firebaseUser) {
          const user = await getDocument(firebaseUser.uid, "users");
          if (user.exists && user.status) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email || "",
              firstName: user.data?.firstName || "",
              lastName: user.data?.lastName || "",
              type:
                user.status && user.exists && user.data?.type
                  ? user.data.type
                  : "user",
              provider: firebaseUser.providerData[0].providerId,
              examsCompleted: user.data?.completed_exams || 0,
              examsAttempted: user.data?.attempted_exams || 0,
              imgId: user.data?.img_id || "",
              imgUrl: user.data?.img_url || "",
            });
          }
        }
        setfetching(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        fetching,
        setfetching,
        setLangSwitch,
        langSwitch,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
